import { createStore } from 'vuex'
import SFetch from '@app/utils/sfetch'

import * as mutationTypes from './mutation-types'

import debounce from 'lodash/debounce'

import { SfLog } from '@app/adapters/logger'
import getPiniaInstance from '@app/shared/pinia/instance'
import { useToastStore } from '@app/shared/pinia/toast'
import { extractServerErrorMessage } from '@app/utils/server-error-message'

import api from '../api/index'
import buildPreviewParams from '../utils/build-preview-params'
import GraphPeriodsInterpreter from '../utils/graph-periods-interpreter'
import periodsRange from '../utils/periods-range'

const interpreter = new GraphPeriodsInterpreter()
const debouncedPreviewGraph = debounce(
  (dispatch) => dispatch('previewGraph'),
  800
)

const LINECHART_GRAPH_TYPE = 'linechart'
const PIECHART_GRAPH_TYPE = 'piechart'
const WATERFALLCHART_GRAPH_TYPE = 'waterfallchart'

// The drawer endpoints answer with the whole drawer state of the template
// (pinned entry, highlighted list, insight list); the editor only tracks
// where its own graph sits in it.
// Saved values come from the response; the pending values follow them, so a
// saved drawer change leaves nothing pending. keepPending holds them back
// while a second drawer call is still to come.
const drawerSetupFromState = (
  drawerState,
  graphId,
  previous,
  { keepPending = false } = {}
) => {
  const pinnedId = drawerState.pinned?.id ?? null
  const pinned = pinnedId === graphId
  const highlighted = drawerState.highlighted.some(
    (entry) => entry.id === graphId
  )
  return {
    ...previous,
    pinned,
    highlighted,
    pendingPinned: keepPending ? previous.pendingPinned : pinned,
    pendingHighlighted: keepPending ? previous.pendingHighlighted : highlighted,
    highlightedCount: drawerState.highlighted.length,
    otherMainChart: pinnedId !== null && pinnedId !== graphId,
  }
}

// Saving would make this graph the main chart while another graph holds
// that role, which is the one drawer change that asks first.
const drawerReplacesMainChart = (drawer) =>
  !!drawer && drawer.pendingPinned && !drawer.pinned && drawer.otherMainChart

const drawerErrorMessage = (error) =>
  extractServerErrorMessage(error) ??
  I18n.t('report.drawer.editor.toggle_error')

const drawerHasPendingChanges = (drawer) =>
  !!drawer &&
  (drawer.pendingPinned !== drawer.pinned ||
    drawer.pendingHighlighted !== drawer.highlighted)

const graphPayloadSnapshot = (getters) =>
  JSON.stringify(getters.formattedReportGraphForSubmit)

const translateGraphValues = (graphValues, reportTags) => {
  return graphValues.map((graphValue, index) => {
    const formulaType = reportTags.includes(graphValue.formula)
      ? 'reportTag'
      : 'formula'

    return {
      key: index,
      id: graphValue.id,
      name: graphValue.name, // is Object like {en: 'series name in english', nl: 'waarschijnlijk'}
      position: graphValue.position,
      budget: graphValue.budget,
      budgetSetId: graphValue.budgetSetId,
      seriesType: graphValue.seriesType,
      valueType: graphValue.valueType,
      delta: graphValue.delta,
      relative: graphValue.relative,
      formulaType: formulaType,
      reportTag: formulaType === 'reportTag' ? graphValue.formula : null,
      formula: formulaType === 'formula' ? graphValue.formula : null,
    }
  })
}

export const actions = {
  setCurrentLocale({ commit }, { locale }) {
    commit(mutationTypes.SET_CURRENT_LOCALE, locale)
  },
  deleteGraph({ state }) {
    api.destroy(state.graphEndpoints.destroyUrl)
  },
  setCurrentTitle({ commit }, { title }) {
    commit(mutationTypes.SET_CURRENT_TITLE, title)
  },
  setDescription({ commit }, { description }) {
    commit(mutationTypes.SET_DESCRIPTION, description)
  },
  setGraphType({ commit, dispatch }, { graphType }) {
    commit(mutationTypes.SET_GRAPH_TYPE, graphType)
    commit(mutationTypes.UPDATE_PREVIEW_DATA, null)
    debouncedPreviewGraph(dispatch)
  },
  setGraphErrors({ commit }, { errors }) {
    commit(mutationTypes.SET_GRAPH_ERRORS, errors)
  },
  setGraphValues({ commit, dispatch }, { graphValues }) {
    commit(mutationTypes.SET_GRAPH_VALUES, graphValues)
    debouncedPreviewGraph(dispatch)
  },
  setMaximumValues({ commit, dispatch }, { maximum }) {
    commit(mutationTypes.SET_MAXIMUM_VALUES, { maximum })
    debouncedPreviewGraph(dispatch)
  },
  setMinimumValues({ commit, dispatch }, { minimum }) {
    commit(mutationTypes.SET_MINIMUM_VALUES, { minimum })
    debouncedPreviewGraph(dispatch)
  },
  setGraphValueName({ commit, dispatch }, { position, name }) {
    commit(mutationTypes.SET_GRAPH_VALUE_NAME, { position, name })
    debouncedPreviewGraph(dispatch)
  },
  setGraphValueAttributes({ commit, dispatch }, { position, attributes }) {
    commit(mutationTypes.SET_GRAPH_VALUE_ATTRIBUTES, { position, attributes })
    const isCollapseOnly =
      Object.keys(attributes).length === 1 &&
      Object.keys(attributes).includes('collapsed')
    if (!isCollapseOnly) {
      debouncedPreviewGraph(dispatch)
    }
  },
  setFrequency({ commit, dispatch }, { frequency }) {
    commit(mutationTypes.SET_FREQUENCY, { frequency })
    debouncedPreviewGraph(dispatch)
  },
  setPriorYears({ commit, dispatch }, { priorYears }) {
    commit(mutationTypes.SET_PRIOR_YEARS, { priorYears })
    debouncedPreviewGraph(dispatch)
  },
  setYearType({ commit, dispatch }, { yearType }) {
    commit(mutationTypes.SET_YEAR_TYPE, { yearType })
    debouncedPreviewGraph(dispatch)
  },
  async previewGraph({ state, getters, commit }) {
    if (!state.preview?.enabled || !state.graphEndpoints?.previewUrl) return

    commit(mutationTypes.UPDATE_PREVIEWING_STATE, true)
    try {
      const params = buildPreviewParams(getters.formattedReportGraphForSubmit)
      const data = await api.previewGraph(
        state.graphEndpoints.previewUrl,
        params
      )
      commit(mutationTypes.UPDATE_PREVIEW_DATA, data)
      commit(mutationTypes.SET_PREVIEW_ERROR, null)
    } catch {
      commit(
        mutationTypes.SET_PREVIEW_ERROR,
        I18n.t('js.report_graph.edit.preview_error')
      )
    } finally {
      commit(mutationTypes.UPDATE_PREVIEWING_STATE, false)
    }
  },
  // Drawer changes ride along with the graph save: the graph is saved first,
  // then the pending pin and highlight are sent to their endpoints. A drawer
  // change that would replace another main chart stops here and asks; the
  // confirm dispatches this action again with replaceMainChart set.
  async saveGraph(
    { state, dispatch, getters, commit },
    { replaceMainChart = false } = {}
  ) {
    if (drawerReplacesMainChart(state.drawer) && !replaceMainChart) {
      commit(mutationTypes.SET_DRAWER_CONFIRMING_REPLACE, true)
      return
    }
    if (state.drawer) commit(mutationTypes.SET_DRAWER_CONFIRMING_REPLACE, false)
    commit(mutationTypes.UPDATE_SAVING_STATE, true)
    try {
      const data = await api.save(
        state.graphEndpoints.submitUrl,
        state.graphEndpoints.submitMethod,
        getters.formattedReportGraphForSubmit
      )
      dispatch('setGraphErrors', { errors: {} })
      commit(
        mutationTypes.SET_GRAPH_VALUES,
        translateGraphValues(data.values, state.reportTemplate.reportTags)
      )
      commit(mutationTypes.SET_OLD_GRAPH_PERIODS, data.periods)
      commit(
        mutationTypes.UPDATE_PREVIEW_DATA,
        JSON.parse(data.renderData || null)
      )
      commit(mutationTypes.SET_GRAPH_ENDPOINTS, data.endpoints)
      commit(
        mutationTypes.SET_SAVED_GRAPH_PAYLOAD,
        graphPayloadSnapshot(getters)
      )
    } catch (error) {
      if (error.status === 422) {
        dispatch('setGraphErrors', { errors: error.body.errors })
      } else if (error.status !== 0) {
        alert('Something went wrong')
      }
      commit(mutationTypes.UPDATE_SAVING_STATE, false)
      return
    }
    commit(mutationTypes.UPDATE_SAVING_STATE, false)
    await dispatch('applyDrawerChanges')
  },
  cancelReplaceMainChart({ commit }) {
    commit(mutationTypes.SET_DRAWER_CONFIRMING_REPLACE, false)
  },
  // Pin before highlight: pinning clears a highlight server-side, and the
  // pending values never hold both, so the second call only runs when the
  // pin left the highlight untouched.
  async applyDrawerChanges({ state, dispatch }) {
    const drawer = state.drawer
    if (!drawer) return
    const pinChanged = drawer.pendingPinned !== drawer.pinned
    const highlightChanged = drawer.pendingHighlighted !== drawer.highlighted
    if (pinChanged) {
      await dispatch('updateDrawer', {
        url: drawer.pinUrl,
        body: { pinned: drawer.pendingPinned },
        keepPending: highlightChanged,
      })
    }
    const after = state.drawer
    if (highlightChanged && after.pendingHighlighted !== after.highlighted) {
      await dispatch('updateDrawer', {
        url: after.highlightUrl,
        body: { highlighted: after.pendingHighlighted },
      })
    }
  },
  setXAxisValue({ commit, dispatch }, { range }) {
    commit(mutationTypes.SET_XAXIS_RANGE, range)
    debouncedPreviewGraph(dispatch)
  },
  // Main chart and pinned metric are mutually exclusive, so turning one on
  // turns the other off, the way the server would on save.
  setDrawerPinned({ commit, state }, { pinned }) {
    commit(mutationTypes.SET_DRAWER_PENDING, {
      pinned,
      highlighted: pinned ? false : state.drawer.pendingHighlighted,
    })
  },
  setDrawerHighlighted({ commit, state }, { highlighted }) {
    commit(mutationTypes.SET_DRAWER_PENDING, {
      pinned: highlighted ? false : state.drawer.pendingPinned,
      highlighted,
    })
  },
  async updateDrawer({ commit, state }, { url, body, keepPending = false }) {
    commit(mutationTypes.UPDATE_DRAWER_SAVING_STATE, true)
    try {
      const drawerState = await api.updateDrawer(url, body)
      commit(
        mutationTypes.SET_DRAWER_SETUP,
        drawerSetupFromState(drawerState, state.graph.id, state.drawer, {
          keepPending,
        })
      )
    } catch (error) {
      useToastStore(getPiniaInstance()).raiseToast({
        id: 'graph-editor-drawer-error',
        message: drawerErrorMessage(error),
        variant: 'danger',
        size: 'md',
        actions: [],
      })
      SfLog.error('Graph editor drawer update failed', {
        error: error instanceof Error ? error : new Error(String(error)),
        extra: { graphId: state.graph.id },
      })
    } finally {
      commit(mutationTypes.UPDATE_DRAWER_SAVING_STATE, false)
    }
  },
}

export default function createGraphEditorStore(props) {
  const store = createStore({
    state() {
      // This is the main store for the graph editor, for all of its components.
      // Considering it's a fairly complex component, some planned structure
      // is needed to make it maintainable.
      //
      // Settings are divided in section and each section has a short comment
      // explaining its goal.
      return {
        // This section is for settings that affect how the editor works but
        // don't affect the graph itself. They're left flat because sub components
        // usually need just one.
        readOnly: props.read_only,
        canEditDescription: props.can_edit_description,
        currentLocale: props.language,
        canSelectBudget: props.can_select_budget,
        saving: false, // To track when we're running the save call
        // The submit payload as last saved. It comes from a getter, so the
        // factory sets it once the store exists.
        savedGraphPayload: null,
        // Backend endpoints related to this graph. Components should not be using this directly.
        graphEndpoints: {
          destroyUrl: props.report_graph_endpoints.destroy_url,
          submitUrl: props.report_graph_endpoints.submit_url,
          submitMethod: props.report_graph_endpoints.submit_method,
          previewUrl: props.report_graph_endpoints.preview_url,
        },
        // Properties that belong directly to the graph object.
        graph: {
          id: props.report_graph.id,
          title: props.report_graph.title, // is Object like {en: 'title in english', nl: 'gedachten'}
          availableLocales: props.report_graph.available_locales,
          graphType: props.report_graph.graph_type,
          description: props.report_graph.description,
          yearType: props.report_graph.year_type,
          maximum: props.report_graph.maximum,
          minimum: props.report_graph.minimum,
        },
        // Validation errors will be populated here
        errors: {},
        // Properties defining the periods (a.k.a. the x-axis)
        oldGraphPeriods: props.graph_periods,
        graphPeriods: {
          ...interpreter.stringsToOptions(
            props.graph_periods,
            props.ledgers_per_year
          ),
          range: periodsRange(props.graph_periods),
          ledgersPerYear: props.ledgers_per_year,
        },
        // Properties defining the value series
        graphValues: translateGraphValues(
          SFetch.camelizeJSON(props.graph_values),
          props.report_template.report_tags
        ),
        // Properties related to the report this graph belongs to
        reportTemplate: {
          id: props.report_template.id,
          name: props.report_template.name,
          reportTags: props.report_template.report_tags,
          editUrl: props.report_template.edit_url,
          graphsUrl: props.report_template.graphs_url,
        },
        // Budget Set properties for budget selection
        budgetSets: props.budget_sets.map((budgetSet) => {
          return {
            id: budgetSet.id,
            name: budgetSet.name,
          }
        }),
        // Dates data for time label next to series name
        datesData: {
          periodFormat: props.dates_data.period_format,
          currentLedgerEndDate: props.dates_data.current_ledger_end_date,
          graphPeriodsRangesBookingYear: props.dates_data.graph_periods_ranges
            ? props.dates_data.graph_periods_ranges.booking_year
            : null,
          graphPeriodsRangesCalendarYear: props.dates_data.graph_periods_ranges
            ? props.dates_data.graph_periods_ranges.calendar_year
            : null,
        },
        // Data relevant for the preview component
        preview: {
          enabled: props.preview.enabled,
          renderData: JSON.parse(props.preview.render_data || null),
          loading: false,
          error: null,
        },
        // Where this graph sits in the report's drawer setup (main chart,
        // pinned metrics). Null when the editor has no drawer controls. The
        // pending pair is what the header shows and what the next save sends.
        drawer: props.drawer
          ? {
              pinned: props.drawer.pinned,
              highlighted: props.drawer.highlighted,
              pendingPinned: props.drawer.pinned,
              pendingHighlighted: props.drawer.highlighted,
              confirmingReplace: false,
              highlightedCount: props.drawer.highlighted_count,
              highlightedLimit: props.drawer.highlighted_limit,
              otherMainChart: props.drawer.other_main_chart,
              pinUrl: props.drawer.pin_url,
              highlightUrl: props.drawer.highlight_url,
              saving: false,
            }
          : null,
      }
    },
    getters: {
      graphCurrentTitle: (state) => {
        return state.graph.title[state.currentLocale]
      },
      graphAvailableLanguages: (state) => {
        return state.graph.availableLocales.map((locale) => {
          return {
            locale: locale,
            language: I18n.t('js.languages')[locale],
          }
        })
      },
      isPersisted: (state) => {
        return !!state.graph.id
      },
      // Compared as the submit payload, so an edit that is typed and reverted
      // counts as saved, and switching the editor language or collapsing a
      // series does not count as a change.
      hasUnsavedChanges: (state, getters) => {
        return (
          drawerHasPendingChanges(state.drawer) ||
          graphPayloadSnapshot(getters) !== state.savedGraphPayload
        )
      },
      isGraphTypeLinechart: (state) => {
        return state.graph.graphType === LINECHART_GRAPH_TYPE
      },
      isGraphTypeCategorical: (state) => {
        return (
          state.graph.graphType === PIECHART_GRAPH_TYPE ||
          state.graph.graphType === WATERFALLCHART_GRAPH_TYPE
        )
      },
      hasSpecificDates: (state) => {
        return !!state.datesData.currentLedgerEndDate
      },
      graphValuesErrors: (state) => {
        return state.errors.reportGraphValues || []
      },
      notEnoughValuesForTimeSeriesOffset: (state) => {
        return state.graphValues.filter((value) => !value._destroy).length < 2
      },
      graphErrors: (state) => {
        return state.errors
      },
      budgetOptions: (state) => {
        if (state.canSelectBudget) {
          const options = [
            {
              value: false,
              name: I18n.t(
                'js.report_graph.edit.graph_values.budget_option_labels.actuals'
              ),
            },
          ]
          state.budgetSets.forEach((budgetSet) =>
            options.push({ value: budgetSet.id, name: budgetSet.name })
          )
          return options
        } else {
          return [
            {
              value: false,
              name: I18n.t(
                'js.report_graph.edit.graph_values.budget_option_labels.actuals'
              ),
            },
            {
              value: true,
              name: I18n.t(
                'js.report_graph.edit.graph_values.budget_option_labels.budget'
              ),
            },
          ]
        }
      },
      formattedReportGraphForSubmit: (state, getters) => {
        const graphPeriodsAttributes = getters.isGraphTypeCategorical
          ? []
          : interpreter.optionsToString({
              ...state.graphPeriods,
              previousPeriods: state.oldGraphPeriods,
            })

        const graphParams = {
          reportGraph: {
            graphType: state.graph.graphType,
            description: state.graph.description,
            maximum: state.graph.maximum,
            minimum: state.graph.minimum,
            yearType: state.graph.yearType,
            reportGraphPeriodsAttributes: graphPeriodsAttributes,
            reportGraphValuesAttributes: state.graphValues.map((value) => {
              const graphValue = {
                id: value.id,
                _destroy: value._destroy,
                position: value.position,
                formula:
                  value.formulaType === 'reportTag'
                    ? value.reportTag
                    : value.formula,
                budget: value.budget,
                budgetSetId: value.budgetSetId,
                seriesType: value.seriesType,
                valueType: value.valueType,
                delta: value.delta,
                relative: value.relative,
              }

              state.graph.availableLocales.forEach(
                (locale) => (graphValue[`name_${locale}`] = value.name[locale])
              )

              return graphValue
            }),
          },
        }

        state.graph.availableLocales.forEach(
          (locale) =>
            (graphParams.reportGraph[`title_${locale}`] =
              state.graph.title[locale])
        )

        return graphParams
      },
    },
    mutations: {
      [mutationTypes.SET_CURRENT_LOCALE](state, locale) {
        state.currentLocale = locale
      },
      [mutationTypes.SET_CURRENT_TITLE](state, title) {
        state.graph.title[state.currentLocale] = title
      },
      [mutationTypes.SET_DESCRIPTION](state, description) {
        state.graph.description = description
      },
      [mutationTypes.SET_GRAPH_TYPE](state, graphType) {
        state.graph.graphType = graphType
      },
      [mutationTypes.SET_GRAPH_VALUES](state, graphValues) {
        state.graphValues = graphValues
      },
      [mutationTypes.SET_MAXIMUM_VALUES](state, { maximum }) {
        state.graph.maximum = maximum
      },
      [mutationTypes.SET_MINIMUM_VALUES](state, { minimum }) {
        state.graph.minimum = minimum
      },
      [mutationTypes.SET_GRAPH_VALUE_NAME](state, { name, position }) {
        const graphValue = state.graphValues.find(
          (value) => value.position === position
        )
        if (graphValue) {
          graphValue.name[state.currentLocale] = name
          state.graphValues.splice(state.graphValues.indexOf(graphValue), 1, {
            ...graphValue,
          })
        }
      },
      [mutationTypes.SET_GRAPH_VALUE_ATTRIBUTES](
        state,
        { attributes, position }
      ) {
        const graphValue = state.graphValues.find(
          (value) => value.position === position
        )
        if (graphValue) {
          state.graphValues.splice(state.graphValues.indexOf(graphValue), 1, {
            ...graphValue,
            ...attributes,
          })
        }
      },
      [mutationTypes.SET_FREQUENCY](state, { frequency }) {
        state.graphPeriods.frequency = frequency
      },
      [mutationTypes.SET_PRIOR_YEARS](state, { priorYears }) {
        state.graphPeriods.priorYears = priorYears
      },
      [mutationTypes.SET_YEAR_TYPE](state, { yearType }) {
        state.graph.yearType = yearType
      },
      [mutationTypes.SET_GRAPH_ERRORS](state, errors) {
        state.errors = errors
      },
      [mutationTypes.SET_XAXIS_RANGE](state, range) {
        state.graphPeriods.range = range
      },
      [mutationTypes.SET_OLD_GRAPH_PERIODS](state, periods) {
        state.oldGraphPeriods = periods
      },
      [mutationTypes.SET_GRAPH_ENDPOINTS](state, endpoints) {
        state.graphEndpoints = endpoints
      },
      [mutationTypes.UPDATE_PREVIEW_DATA](state, renderData) {
        state.preview.renderData = renderData
      },
      [mutationTypes.UPDATE_PREVIEWING_STATE](state, loading) {
        state.preview.loading = loading
      },
      [mutationTypes.SET_PREVIEW_ERROR](state, error) {
        state.preview.error = error
      },
      [mutationTypes.UPDATE_SAVING_STATE](state, saving) {
        state.saving = saving
      },
      [mutationTypes.SET_DRAWER_SETUP](state, drawer) {
        state.drawer = drawer
      },
      [mutationTypes.UPDATE_DRAWER_SAVING_STATE](state, saving) {
        state.drawer.saving = saving
      },
      [mutationTypes.SET_DRAWER_PENDING](state, { pinned, highlighted }) {
        state.drawer.pendingPinned = pinned
        state.drawer.pendingHighlighted = highlighted
      },
      [mutationTypes.SET_DRAWER_CONFIRMING_REPLACE](state, confirming) {
        state.drawer.confirmingReplace = confirming
      },
      [mutationTypes.SET_SAVED_GRAPH_PAYLOAD](state, payload) {
        state.savedGraphPayload = payload
      },
    },
    actions,
  })
  store.commit(
    mutationTypes.SET_SAVED_GRAPH_PAYLOAD,
    graphPayloadSnapshot(store.getters)
  )
  return store
}
